import { postTradingEnsureReady } from '@/api/privy-trading-ready';
import { getAccessToken, getWalletAddress } from '@/utils/auth-token';
import { isTradingChannelPreparingError } from '@/utils/trade-error-map';
import { sessionExpectsCustodialWallet } from '@/utils/session-login-track';

/** ensure-ready 因 Gas 未到账失败时的退避（与后端 ~12s 轮询衔接） */
const GAS_WAIT_RETRY_DELAYS_MS = [2500, 3500, 4500];

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** session 内已 ready 的托管地址（小写）；避免同会话重复 ensure-ready */
let readyAddressCache = '';

/** 进行中的 ensure-ready Promise；弹窗预热与下单并发时合并为一次 HTTP */
let inflightEnsurePromise = null;

export function clearTradingReadyCache() {
  readyAddressCache = '';
  inflightEnsurePromise = null;
}

function normalizeAddr(value) {
  return String(value ?? '')
    .trim()
    .toLowerCase();
}

/**
 * 后端 ensure-ready 是否已确认链上授权全部就绪，可跳过前端二次 approve。
 *
 * 安全边界：
 * - skippedByCache：同会话曾成功 ready（含 MaxUint256 / setApprovalForAll）
 * - 非空 steps 且无一为 SUBMITTED（刚广播未确认的 tx 仍须走二次校验）
 * - ready=true 但 steps 为空且无 cache → 不信任，不跳过
 *
 * @param {{ ready?: boolean, steps?: Array, skippedByCache?: boolean }|null|undefined} vo
 * @returns {boolean}
 */
export function isCustodialTradingFullyReady(vo) {
  if (!vo || vo.ready !== true) return false;
  if (vo.skippedByCache === true) return true;
  const steps = Array.isArray(vo.steps) ? vo.steps : null;
  if (!steps || steps.length === 0) return false;
  return steps.every((step) => {
    const status = String(step?.status ?? '')
      .trim()
      .toUpperCase();
    if (status === 'SUBMITTED') return false;
    if (step?.skipped === true) return true;
    return status === 'SKIPPED' || status === 'OK';
  });
}

function rememberReadyCache(vo, hint) {
  const resolved = normalizeAddr(vo?.address || hint);
  if (!resolved || vo?.ready !== true) {
    return;
  }
  if (isCustodialTradingFullyReady(vo)) {
    // 仅全 SKIPPED/OK 才写入 session cache；含 SUBMITTED 时下次仍须重查
    readyAddressCache = resolved;
    return;
  }
  // 有待确认 approve：勿缓存，避免跳过二次校验
  readyAddressCache = '';
}

/**
 * 绑定后 / 托管下单前：确保 gas + allowance 通道就绪。
 * 失败抛错（提交路径应阻断）；绑定后可 fire-and-forget。
 *
 * @param {{ force?: boolean, address?: string, autoRetryOnGasWait?: boolean }} [options]
 * @returns {Promise<{ address?: string, ready?: boolean, steps?: Array, skippedByCache?: boolean }|null>}
 */
export async function ensurePrivyTradingReady({
  force = false,
  address,
  autoRetryOnGasWait = false,
} = {}) {
  if (!getAccessToken()) {
    throw new Error('Platform session required before trading ensure-ready');
  }
  const hint = normalizeAddr(address || getWalletAddress());
  if (!force && hint && readyAddressCache && readyAddressCache === hint) {
    return { address: hint, ready: true, steps: [], skippedByCache: true };
  }

  // 同会话并发（弹窗预热 + 点击下单）合并为一次 POST，避免双倍 Privy/RPC
  if (!force && inflightEnsurePromise) {
    return inflightEnsurePromise;
  }

  const run = (async () => {
    const maxAttempts = autoRetryOnGasWait ? 1 + GAS_WAIT_RETRY_DELAYS_MS.length : 1;
    let lastErr = null;
    for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
      if (attempt > 0) {
        readyAddressCache = '';
        await sleep(GAS_WAIT_RETRY_DELAYS_MS[attempt - 1] ?? 3000);
      }
      try {
        const vo = await postTradingEnsureReady();
        rememberReadyCache(vo, hint);
        return vo;
      } catch (err) {
        lastErr = err;
        const canRetry =
          autoRetryOnGasWait &&
          attempt < maxAttempts - 1 &&
          isTradingChannelPreparingError(err);
        if (!canRetry) {
          throw err;
        }
        console.info('[privy-trading-ready] gas wait, retry ensure-ready', {
          attempt: attempt + 1,
          maxAttempts,
        });
      }
    }
    throw lastErr ?? new Error('ensure-ready failed');
  })();

  if (!force) {
    inflightEnsurePromise = run;
    void run.finally(() => {
      if (inflightEnsurePromise === run) {
        inflightEnsurePromise = null;
      }
    });
  }

  return run;
}

/**
 * 绑定后异步预热：失败只打日志，不阻断登录。
 */
export function warmPrivyTradingReadyAfterBind(address) {
  void ensurePrivyTradingReady({ force: true, address }).catch((err) => {
    console.warn('[privy-trading-ready] post-bind ensure-ready failed', err);
  });
}

/**
 * 托管 Session 静默预热（打开交易弹窗 / 恢复会话）。
 * 非托管或未登录直接 no-op。
 */
export function warmPrivyTradingReadyIfCustodial(address) {
  if (!sessionExpectsCustodialWallet()) return;
  if (!getAccessToken()) return;
  void ensurePrivyTradingReady({ force: false, address }).catch((err) => {
    console.warn('[privy-trading-ready] custodial warm ensure-ready failed', err);
  });
}
